import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react"

import ReactMarkdown from "react-markdown"

import remarkBreaks from "remark-breaks"
import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"
import rehypeSlug from "rehype-slug"

import {
  Box,
  Button,
  Breadcrumbs,
  Collapse,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Link as MuiLink,
  styled,
  Typography,
} from "@mui/material"

import {
  DescriptionOutlined,
  ExpandLess,
  ExpandMore,
  NavigateNextRounded,
  ZoomInRounded,
} from "@mui/icons-material"

import HKVDialog from "../../HKVDialog/7.0"

import "./HKVMarkdown.css"

const Sidebar = styled("aside")(({ theme }) => ({
  flex: "0 0 260px",
  alignSelf: "stretch",
  overflowY: "auto",
  padding: theme.spacing(2, 1.5),
  borderRight: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down("md")]: {
    flex: "0 0 auto",
    width: "100%",
    maxHeight: "none",
    borderRight: "none",
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2),
  },
}))

const Content = styled("div")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: "auto",
  padding: theme.spacing(2),
}))

const Layout = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "stretch",
  width: "100%",
  height: "100%",
  minHeight: 0,
  [`& ${Content}, & ${Sidebar}`]: {
    overflowY: "auto",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    overflowY: "auto",
    [`& ${Content}, & ${Sidebar}`]: {
      overflowY: "hidden",
    },
  },
}))

const SidebarHeading = styled(Typography)(({ theme }) => ({
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  padding: theme.spacing(1, 1.5, 0.5),
}))

const NavItem = styled(ListItemButton, {
  shouldForwardProp: (p) => p !== "active" && p !== "depth",
})(({ theme, active, depth = 0 }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: 2,
  paddingLeft: theme.spacing(1.5 + depth * 1.5),
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  backgroundColor: active ? theme.palette.action.selected : "transparent",
  fontWeight: active ? 600 : 400,
  position: "relative",
  "&::before": active
    ? {
      content: '""',
      position: "absolute",
      left: 0,
      top: 6,
      bottom: 6,
      width: 3,
      borderRadius: 2,
      backgroundColor: theme.palette.primary.main,
    }
    : {},
  "& .MuiListItemText-primary": {
    fontSize: "0.875rem",
    fontWeight: "inherit",
  },
  "& .nav-icon": {
    fontSize: 16,
    marginRight: theme.spacing(1),
    opacity: 0.6,
  },
}))

const StyledSpan = styled("span")(({ theme }) => ({
  display: "inline-flex",
  verticalAlign: "middle",
  width: "fit-content",
  height: "fit-content",
  position: "relative",
}))

const createKey = (text) => {
  return text?.trim()?.replaceAll(" ", "_")?.replaceAll("?", "")?.toLowerCase()
}

const createId = (data) => {
  if (Array.isArray(data)) {
    return data.map((d) => createId(d))
  } else if (typeof data === "string") {
    return createKey(data)
  } else {
    if (data?.hasOwnProperty?.("props")) {
      return data.props.children
    } else {
      return
    }
  }
}

const containsFile = (nodes, file) =>
  nodes?.some((n) =>
    n.children ? containsFile(n.children, file) : n.file === file
  )

const findPath = (items, target, path = []) => {
  for (const item of items || []) {
    if (item.children) {
      const found = findPath(item.children, target, [...path, item.label])
      if (found) return found
    } else if (item.file === target) {
      return [...path, item.label]
    }
  }
  return null
}

const findFirstFile = (items) => {
  for (const item of items || []) {
    if (item.children) {
      const found = findFirstFile(item.children)
      if (found) return found
    } else if (item.file) {
      return item.file
    }
  }
  return null
}

const readHashFile = () => {
  if (typeof window === "undefined") return null
  const raw = decodeURIComponent(window.location.hash || "").replace(/^#/, "")
  return raw || null
}

const NavTree = ({ items, currentFile, onNavigate, depth = 0 }) => {
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {}
    const walk = (nodes) => {
      nodes?.forEach((n) => {
        if (n.children) {
          if (containsFile(n.children, currentFile)) initial[n.label] = true
          walk(n.children)
        }
      })
    }
    walk(items)
    return initial
  })

  const toggle = (label) =>
    setOpenGroups((s) => ({ ...s, [label]: !s[label] }))

  return (
    <List dense disablePadding>
      {items.map((item) => {
        if (item.children) {
          const open = !!openGroups[item.label]
          return (
            <React.Fragment key={item.label}>
              <NavItem depth={depth} onClick={() => toggle(item.label)}>
                <ListItemText primary={item.label} />
                {open ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
              </NavItem>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <NavTree
                  items={item.children}
                  currentFile={currentFile}
                  onNavigate={onNavigate}
                  depth={depth + 1}
                />
              </Collapse>
            </React.Fragment>
          )
        }

        const active = item.file === currentFile
        return (
          <NavItem
            key={item.file}
            depth={depth}
            active={active}
            onClick={() => onNavigate(item.file)}
          >
            <DescriptionOutlined className="nav-icon" />
            <ListItemText primary={item.label} />
          </NavItem>
        )
      })}
    </List>
  )
}

const HKVMarkdown = ({
  className,
  source,
  files,
  initialFile,
  navigation,
  showBreadcrumbs = true,
  useHash = true, // sync current file with window.location.hash
  ...rest
}) => {
  const isMultiFile = !!files

  // pick initial file: URL hash > prop > first file in nav > undefined
  const computeInitial = () => {
    if (!isMultiFile) return undefined
    if (useHash) {
      const fromHash = readHashFile()
      if (fromHash && files[fromHash]) return fromHash
    }
    if (initialFile && files[initialFile]) return initialFile
    const firstInTree = findFirstFile(navigation?.items)
    if (firstInTree && files[firstInTree]) return firstInTree
    return initialFile
  }

  const [currentFile, setCurrentFile] = useState(computeInitial)
  const [dialogConfig, setDialogConfig] = useState()
  const contentRef = useRef(null)

  // listen for hash changes (back/forward, manual edits, external links)
  useEffect(() => {
    if (!isMultiFile || !useHash) return
    const onHashChange = () => {
      const fromHash = readHashFile()
      if (fromHash && files[fromHash] && fromHash !== currentFile) {
        setCurrentFile(fromHash)
        contentRef.current?.scrollTo({ top: 0 })
      }
    }
    window.addEventListener("hashchange", onHashChange)
    return () => window.removeEventListener("hashchange", onHashChange)
  }, [isMultiFile, useHash, files, currentFile])

  // if files loaded after mount and currentFile is unset, sync it
  useEffect(() => {
    if (!isMultiFile) return
    if (currentFile && files[currentFile]) return
    const next = computeInitial()
    if (next) setCurrentFile(next)
  }, [files])

  const resolvedSource = isMultiFile
    ? files[currentFile] ??
    `# Not found\n\nThe file \`${currentFile}\` could not be loaded.`
    : source ?? ""

  const breadcrumbPath = useMemo(
    () => findPath(navigation?.items, currentFile) || [],
    [navigation, currentFile]
  )

  const navigateTo = useCallback(
    (filename, anchor = null) => {
      const switchingFile = filename !== currentFile

      if (switchingFile) {
        setCurrentFile(filename)
        if (useHash && typeof window !== "undefined") {
          window.location.hash = encodeURIComponent(filename)
        }
      }

      // scroll: to anchor if provided, otherwise top
      // wait a tick so the new content renders before we try to find the anchor
      setTimeout(() => {
        if (anchor) {
          const target =
            contentRef.current?.querySelector(`#${CSS.escape(anchor)}`) ||
            contentRef.current?.querySelector(`[name="${anchor}"]`)
          if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" })
            return
          }
        }
        contentRef.current?.scrollTo({ top: 0, behavior: switchingFile ? "auto" : "smooth" })
      }, switchingFile ? 50 : 0)
    },
    [currentFile, useHash]
  )

  const openDialog = (size, options) => {
    const mergedOptions = { ...options, open: true, size: size ? size : "lg" }
    setDialogConfig(mergedOptions)
  }

  const closeDialog = () => {
    setDialogConfig()
  }

  const renderImageDialog = () => {
    if (!dialogConfig) return

    const { imgSrc } = dialogConfig

    return (
      <HKVDialog
        config={dialogConfig}
        onClose={closeDialog}
        sx={{ zIndex: 1600, cursor: "zoom-out" }}
        noPaper
        closeOnBackdropClick
      >
        <img
          title={imgSrc}
          alt={imgSrc}
          src={imgSrc}
          onClick={closeDialog}
          style={{
            objectFit: "contain",
            height: "fit-content",
            width: "fit-content",
            margin: "auto",
            maxWidth: "calc(100vw - 16px)",
            maxHeight: "calc(100vh - 16px)"
          }}
        />
      </HKVDialog>
    )
  }

  const renderedMarkdown = useMemo(
    () => (
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw, rehypeSlug]}
        urlTransform={(url) => url}
        components={{
          table: ({ node, ...r }) => (
            <div
              style={{
                width: "100%",
                maxWidth: "100vw",
                overflowX: "auto",
                overflowY: "hidden",
              }}
            >
              <table {...r} />
            </div>
          ),
          img: ({ src, alt, title, node, ...r }) => {
            if (!src) return null

            // parse "WxH" from title slot
            let width = "fit-content"
            let height = "fit-content"
            const sizeMatch = title?.match(/^(\d*)x(\d*)$/)
            if (sizeMatch) {
              width = sizeMatch[1] ? Number(sizeMatch[1]) : "fit-content"
              height = sizeMatch[2] ? Number(sizeMatch[2]) : "fit-content"
            }

            return (
              <StyledSpan
                onClick={() => openDialog("full", { imgSrc: src })}
              >
                <img
                  src={src}
                  title={alt}
                  alt={alt}
                  width={width}
                  height={height}
                  {...r}
                />
                {/* <span className="zoom-icon">
                  <ZoomInRounded />
                </span> */}
              </StyledSpan>
            )
          },
          a: ({ href, title, name, children, node, ...r }) => {
            if (!href && name) {
              return (
                <span id={name} {...r}>
                  {children}
                </span>
              )
            }

            // Cookie-popup opnieuw openen
            if (href === "#cookie-instellingen") {
              return (
                <Typography
                  component="a"
                  variant="inherit"
                  color="primary"
                  href={href}
                  title={createId(children)}
                  onClick={(e) => {
                    e.preventDefault()
                    window.dispatchEvent(new Event("hkv-open-cookie-settings"))
                  }}
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    font: "inherit",
                  }}
                >
                  {children}
                </Typography>
              )
            }

            if (href?.startsWith("#")) {
              return (
                <Typography
                  component="a"
                  variant="inherit"
                  color="primary"
                  href={href}
                  title={createId(children)}
                  onClick={(e) => {
                    // smooth-scroll to the anchor inside the Content area
                    e.preventDefault()
                    const id = decodeURIComponent(href.slice(1))
                    // try id first, then legacy <a name="..."> attribute
                    const target =
                      contentRef.current?.querySelector(`#${CSS.escape(id)}`) ||
                      contentRef.current?.querySelector(`a[name="${id}"]`)
                    target?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }}
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    font: "inherit",
                  }}
                  {...r}
                >
                  {children}
                </Typography>
              )
            }

            const mdLinkMatch =
              isMultiFile &&
              href &&
              !/^(https?:|mailto:)/i.test(href) &&
              href.match(/^([^#?]+\.md)(#(.+))?$/i)

            if (mdLinkMatch) {
              const filename = decodeURIComponent(mdLinkMatch[1].split("/").pop())
              const anchor = mdLinkMatch[3] || null

              return (
                <Typography
                  component="a"
                  variant="inherit"
                  color="primary"
                  href={`#${encodeURIComponent(filename)}${anchor ? `#${anchor}` : ""}`}
                  title={createId(children)}
                  onClick={(e) => {
                    e.preventDefault()
                    navigateTo(filename, anchor)
                  }}
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    font: "inherit",
                  }}
                  {...r}
                >
                  {children}
                </Typography>
              )
            }

            return (
              <Typography
                component="a"
                variant="inherit"
                color="primary"
                href={href}
                title={createId(children)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: "underline",
                  font: "inherit",
                }}
                {...r}
              >
                {children}
              </Typography>
            )
          },
          // render <p> as <div> so block children (lists, tables, images) are valid
          p: ({ node, ...r }) => <Typography component="div" className="md-paragraph" {...r} />,
          li: ({ node, ...r }) => (
            <Typography component="li" {...r} />
          ),
          h1: ({ node, ...r }) => <Typography {...r} component="h1" sx={{ fontWeight: 500 }} fontSize={28} />,
          h2: ({ node, ...r }) => <Typography {...r} component="h2" sx={{ fontWeight: 500 }} fontSize={26} />,
          h3: ({ node, ...r }) => <Typography {...r} component="h3" sx={{ fontWeight: 500 }} fontSize={24} />,
          h4: ({ node, ...r }) => <Typography {...r} component="h4" sx={{ fontWeight: 500 }} fontSize={22} />,
          h5: ({ node, ...r }) => <Typography {...r} component="h5" sx={{ fontWeight: 500 }} fontSize={20} />,
          h6: ({ node, ...r }) => <Typography {...r} component="h6" sx={{ fontWeight: 500 }} fontSize={18} />,
        }}
        children={resolvedSource}
      />
    ),
    [resolvedSource, navigateTo, isMultiFile]
  )

  if (!isMultiFile) {
    return (
      <React.Fragment>
        <div
          {...rest}
          className={className}
        >
          {renderedMarkdown}
        </div>
        {renderImageDialog()}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Layout {...rest}>
        {navigation?.items?.length > 0 && (
          <Sidebar>
            {navigation.title && (
              <SidebarHeading>{navigation.title}</SidebarHeading>
            )}
            <NavTree
              items={navigation.items}
              currentFile={currentFile}
              onNavigate={navigateTo}
            />
          </Sidebar>
        )}
        <Content ref={contentRef} className={className}>
          {showBreadcrumbs && breadcrumbPath.length > 1 && (
            <Breadcrumbs
              separator={<NavigateNextRounded fontSize="small" />}
              sx={{ mb: 2 }}
            >
              {breadcrumbPath.map((label, idx) => {
                const isLast = idx === breadcrumbPath.length - 1
                return (
                  <Typography
                    key={label + idx}
                    color={isLast ? "text.primary" : "text.secondary"}
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: isLast ? 600 : 400,
                    }}
                  >
                    {label}
                  </Typography>
                )
              })}
            </Breadcrumbs>
          )}
          {renderedMarkdown}
        </Content>
      </Layout>
      {renderImageDialog()}
    </React.Fragment>
  )
}

export default React.memo(HKVMarkdown)